import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, injectIntl } from 'react-intl'

import {
  SelectInput,
  isNumber,
} from '../Inputs/Inputs'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'

const options = {
  test: [
    { value: 'greaterThan', name: 'Greater Than' },
    { value: 'lessThan', name: 'Less Than' },
  ],
  bin: [
    { value: true, name: 'Full' },
    { value: false, name: 'Empty' },
  ],
}

function Bindicator ({ intl, ...props }) {
  const [state, setState] = useState({
    test: props.test,
    formula: props.formula,
    valueKey: props.valueKey,
    testValue: props.testValue,
    testValue1: !!(props.testValue & 1),
    testValue2: !!(props.testValue & 2),
    testValue3: !!(props.testValue & 4),
    testValue4: !!(props.testValue & 8),
    multiplier: props.multiplier,
    body: props.body,
    subject: props.subject,
    debounceTime: props.debounceTime,
    notificationInterval: props.notificationInterval,
    sendOnce: props.sendOnce,
    type: props.type,
    powered: (props.device && props.device.physical && props.device.physical.powered === true),
    invalid: false,
    testValueValidation: (props.testValueDisplay || ((arg) => arg))(props.testValue, props.locale, 1),
  })

  const updateNewRule = useCallback(() => {
    AppDispatcher.dispatch({
      action: 'update-new-rule-parameters',
      template: props.template,
      test: state.test,
      formula: state.formula,
      valueKey: state.valueKey,
      testValue: state.testValue,
      multiplier: state.multiplier,
      body: state.body,
      subject: state.subject,
      debounceTime: state.debounceTime,
      sendOnce: state.sendOnce,
      notificationInterval: state.notificationInterval,
      type: state.type,
      invalid: state.invalid,
    })
  }, [props.template, state])

  useEffect(() => {
    updateNewRule()
  }, [updateNewRule])

  const handleChange = useCallback((field, value, callback) => {
    let valueU = value
    if (field === 'sendOnce') {
      valueU = value === 'true'
    }

    setState((prevState) => ({
      ...prevState,
      [field]: valueU,
    }))

    if (callback) setTimeout(callback, 0)
  }, [])

  const handleChangeInterval = useCallback((field, value, callback) => {
    setState((prevState) => ({
      ...prevState,
      [field]: Number(value),
    }))

    if (callback) setTimeout(callback, 0)
  }, [])

  const handleChangeDecimal = useCallback((field, value, callback) => {
    const valueNumber = Number(value)
    const valueCorrect = isNumber(valueNumber)

    if (valueCorrect) {
      setState((prevState) => ({
        ...prevState,
        [field]: (props.testValueDisplayReverse || ((arg) => arg))(valueNumber, props.locale, 2),
        invalid: !valueCorrect,
        testValueValidation: value,
      }))
    } else {
      setState((prevState) => ({
        ...prevState,
        invalid: !valueCorrect,
        testValueValidation: value,
      }))
    }

    if (callback) setTimeout(callback, 0)
  }, [props.testValueDisplayReverse, props.locale])

  const handleChangeCheckbox = useCallback((field, value, callback) => {
    const newValue = !state[field]

    setState((prevState) => ({
      ...prevState,
      [field]: newValue,
    }))

    setTimeout(() => {
      setState((prevState) => {
        let newTestValue = 0
        if (field === 'testValue1' ? newValue : prevState.testValue1) { newTestValue |= 1 }
        if (field === 'testValue2' ? newValue : prevState.testValue2) { newTestValue |= 2 }
        if (field === 'testValue3' ? newValue : prevState.testValue3) { newTestValue |= 4 }
        if (field === 'testValue4' ? newValue : prevState.testValue4) { newTestValue |= 8 }

        return {
          ...prevState,
          testValue: newTestValue,
        }
      })

      if (callback) setTimeout(callback, 0)
    }, 0)
  }, [state])

  const childChange = useCallback((event) => {
    switch (event.target.name) {
      case 'testValue1':
      case 'testValue2':
      case 'testValue3':
      case 'testValue4':
        handleChangeCheckbox(event.target.name, event.target.value)
        break
      case 'testValue':
        handleChangeDecimal(event.target.name, event.target.value)
        break
      case 'test':
      case 'valueKey':
      case 'method':
      case 'sendOnce':
        handleChange(event.target.name, event.target.value)
        break
      case 'notificationInterval':
        handleChangeInterval(event.target.name, event.target.value)
        break
      default:
        break
    }
  }, [handleChangeCheckbox, handleChangeDecimal, handleChange, handleChangeInterval])

  const { formatMessage } = intl

  let testDisplay = 'less than'
  switch (props.test) {
    case 'greaterThan':
      testDisplay = 'greater than'
      break
    case 'equalTo':
      testDisplay = 'equal to'
      break
    case 'lessThan':
    default:
      testDisplay = 'less than'
      break
  }

  let checkboxes = (
    <div className={styles.ruleCheckbox}>
      <section className={styles.ruleInlineSelect}>
        <div>
          Input 1
        </div>
        <div className={styles.ruleSelectSmall}>
          <SelectInput
            value={state.testValue1}
            field='testValue1'
            childChange={childChange}
            options={options.bin}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
      <section className={styles.ruleInlineSelect}>
        <div>
          Input 2
        </div>
        <div className={styles.ruleSelectSmall}>
          <SelectInput
            value={state.testValue2}
            field='testValue2'
            childChange={childChange}
            options={options.bin}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
      <section className={styles.ruleInlineSelect}>
        <div>
          Input 3
        </div>
        <div className={styles.ruleSelectSmall}>
          <SelectInput
            value={state.testValue3}
            field='testValue3'
            childChange={childChange}
            options={options.bin}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
      <section className={styles.ruleInlineSelect}>
        <div>
          Input 4
        </div>
        <div className={styles.ruleSelectSmall}>
          <SelectInput
            value={state.testValue4}
            field='testValue4'
            childChange={childChange}
            options={options.bin}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
    </div>
  )

  if (state.powered) {
    checkboxes = (
      <div>
        <section className={styles.ruleInlineSelect}>
          <div>
            Input 1
          </div>
          <div className={styles.ruleSelectSmall}>
            <SelectInput
              value={state.testValue2}
              field='testValue2'
              childChange={childChange}
              options={options.bin}
              style={styles.ruleSelectInput}
            />
          </div>
        </section>
        <section className={styles.ruleInlineSelect}>
          <div>
            Input 2
          </div>
          <div className={styles.ruleSelectSmall}>
            <SelectInput
              value={state.testValue3}
              field='testValue3'
              childChange={childChange}
              options={options.bin}
              style={styles.ruleSelectInput}
            />
          </div>
        </section>
        <section className={styles.ruleInlineSelect}>
          <div>
            Input 3
          </div>
          <div className={styles.ruleSelectSmall}>
            <SelectInput
              value={state.testValue4}
              field='testValue4'
              childChange={childChange}
              options={options.bin}
              style={styles.ruleSelectInput}
            />
          </div>
        </section>
      </div>
    )
  }

  return (
    <div>
      <section>
        <h5>
          Alert me when
          {' '}
          {props.sensor}
          {' '}
          is
          {' '}
          {testDisplay}
        </h5>
        {checkboxes}
      </section>
      <section>
        <FormattedMessage
          id='rules.snooze.name'
          defaultMessage='Snooze Alerts'
          description='Snooze Alerts heading'
          tagName='h5'
        />
        <div className={styles.ruleSelect}>
          <SelectInput
            value={state.notificationInterval}
            field='notificationInterval'
            childChange={childChange}
            options={[
              {
                value: 0,
                name: formatMessage({
                  id: 'rules.reminders.never',
                  defaultMessage: 'None',
                  description: 'None option',
                }),
              },
              {
                value: 900000,
                name: formatMessage({
                  id: 'rules.reminders.every15Min',
                  defaultMessage: '15 minutes',
                  description: '15 minutes option',
                }),
              },
              {
                value: 3600000,
                name: formatMessage({
                  id: 'rules.reminders.every1Hrs',
                  defaultMessage: '1 hour',
                  description: '1 hour option',
                }),
              },
              {
                value: 14400000,
                name: formatMessage({
                  id: 'rules.reminders.every4Hrs',
                  defaultMessage: '4 hours',
                  description: '4 hours option',
                }),
              },
              {
                value: 64800000,
                name: formatMessage({
                  id: 'rules.reminders.every18Hrs',
                  defaultMessage: '18 hours',
                  description: '18 hours option',
                }),
              },
              {
                value: 86400000,
                name: formatMessage({
                  id: 'rules.reminders.every24Hrs',
                  defaultMessage: '24 hours',
                  description: '24 hours option',
                }),
              },
            ]}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
      <section>
        <FormattedMessage
          id='rules.reminders.name'
          defaultMessage='Send Reminders'
          description='Send Reminders heading'
          tagName='h5'
        />
        <div className={styles.ruleSelect}>
          <SelectInput
            value={state.sendOnce}
            field='sendOnce'
            childChange={childChange}
            options={[
              {
                value: true,
                name: formatMessage({
                  id: 'rules.sendOnce.off',
                  defaultMessage: 'Off',
                  description: 'Off option',
                }),
              },
              {
                value: false,
                name: formatMessage({
                  id: 'rules.sendOnce.on',
                  defaultMessage: 'On',
                  description: 'On option',
                }),
              },
            ]}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
    </div>
  )
}

Bindicator.propTypes = {
  rule: PropTypes.object,
  intl: PropTypes.object.isRequired,
  test: PropTypes.string,
  formula: PropTypes.string,
  valueKey: PropTypes.string,
  testValue: PropTypes.number,
  multiplier: PropTypes.number,
  body: PropTypes.string,
  subject: PropTypes.string,
  debounceTime: PropTypes.number,
  notificationInterval: PropTypes.number,
  sendOnce: PropTypes.bool,
  type: PropTypes.string,
  device: PropTypes.object,
  locale: PropTypes.string,
  sensor: PropTypes.string,
  template: PropTypes.string,
  testValueDisplay: PropTypes.func,
  testValueDisplayReverse: PropTypes.func,
}


export default injectIntl(Bindicator)
